import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useEspecialidades } from "./Especialidades/useEspecialidades";
import { useEspecialidadesSelection } from "./Especialidades/useEspecialidadesSelection";
import { COLORS } from "./Especialidades/styles/configuracionOA.styles";
import { EspecialidadesTransferList } from "./Especialidades/EspecialidadesTransferList";

type UserDataWithEspecialidades = {
  especialidades: string[];
  [key: string]: any;
};

interface ConfiguracionObjetivosAprendizajeProps {
  title?: string;
  disabled?: boolean;
  setUserData: React.Dispatch<React.SetStateAction<UserDataWithEspecialidades>>;
  userEspecialidades?: string[];
}

const ConfiguracionObjetivosAprendizaje: React.FC<
  ConfiguracionObjetivosAprendizajeProps
> = ({
  title = "Configuración Especialidades",
  disabled = false,
  setUserData,
  userEspecialidades = [],
}) => {
  const { data: especialidades, loading, error } = useEspecialidades();

  const {
    selectedIds,
    selectedCount,
    moveToSelected,
    moveToAvailable,
  } = useEspecialidadesSelection({
    especialidades,
    initialSelected: userEspecialidades,
    disabled: disabled || loading,
  });

  const [availableQuery, setAvailableQuery] = useState("");
  const [selectedQuery, setSelectedQuery] = useState("");

  const selectedSet = selectedIds;

  const availableEspecialidades = useMemo(() => {
    return especialidades.filter((item) => !selectedSet.has(item.id));
  }, [especialidades, selectedSet]);

  const selectedEspecialidades = useMemo(() => {
    return especialidades.filter((item) => selectedSet.has(item.id));
  }, [especialidades, selectedSet]);

  const filteredAvailable = useMemo(() => {
    const q = availableQuery.trim().toLowerCase();
    if (!q) return availableEspecialidades;

    return availableEspecialidades.filter((o) =>
      o.label.toLowerCase().includes(q)
    );
  }, [availableEspecialidades, availableQuery]);

  const filteredSelected = useMemo(() => {
    const q = selectedQuery.trim().toLowerCase();
    if (!q) return selectedEspecialidades;

    return selectedEspecialidades.filter((o) =>
      o.label.toLowerCase().includes(q)
    );
  }, [selectedEspecialidades, selectedQuery]);

  useEffect(() => {
    const nextIds = Array.from(selectedIds);

    setUserData((prev) => {
      const prevIds = Array.isArray(prev.especialidades)
        ? prev.especialidades
        : [];

      const sameLength = prevIds.length === nextIds.length;
      const sameItems =
        sameLength && prevIds.every((id) => selectedIds.has(id));

      if (sameItems) return prev;

      return {
        ...prev,
        especialidades: nextIds,
      };
    });
  }, [selectedIds, setUserData]);

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: COLORS.bg,
        borderRadius: "8px",
        p: { xs: 2, sm: 3 },
      }}
    >
      <Typography
        sx={{
          fontFamily: "Montserrat",
          fontSize: { xs: "1.1rem", sm: "24px" },
          fontWeight: 800,
          color: COLORS.title,
          mb: 2,
        }}
      >
        {title}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <EspecialidadesTransferList
              selectedCount={selectedCount}
              availableQuery={availableQuery}
              selectedQuery={selectedQuery}
              onAvailableQueryChange={setAvailableQuery}
              onSelectedQueryChange={setSelectedQuery}
              availableItems={filteredAvailable}
              selectedItems={filteredSelected}
              onMoveToSelected={moveToSelected}
              onMoveToAvailable={moveToAvailable}
              loading={loading}
              error={error}
              disabled={disabled}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ConfiguracionObjetivosAprendizaje;